import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import _ from 'lodash';
import { buildDisplayName } from '../../utils/nameFormatting';
import '../../styles/components/reusable/SearchBar.css';

const SearchBar = () => {
    const [searchTerm, setSearchTerm] = useState('');
    const [results, setResults] = useState([]);
    const [isDropdownVisible, setIsDropdownVisible] = useState(false);
    const navigate = useNavigate();
    const searchBarRef = useRef(null);

    const fetchSearchResults = useCallback(
        _.debounce((query) => {
            if (!query.trim()) {
                setResults([]);
                return;
            }

            fetch(`https://dirabbisv7.execute-api.eu-west-2.amazonaws.com/dev/content?search=${encodeURIComponent(query.trim())}`)
                .then((response) => response.json())
                .then((data) => {
                    setResults(
                        data.map((user) => ({
                            givenName: user[0],
                            familyName: user[1],
                            role: user[2],
                            profileSlug: user[3],
                        }))
                    );
                })
                .catch((error) => console.error('Error fetching search data:', error));
        }, 300),
        []
    );

    useEffect(() => {
        fetchSearchResults(searchTerm);
        return () => fetchSearchResults.cancel();
    }, [searchTerm, fetchSearchResults]);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (searchBarRef.current && !searchBarRef.current.contains(event.target)) {
                setIsDropdownVisible(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const handleResultClick = (profileSlug) => {
        navigate(`/profile/${profileSlug}`);
        setIsDropdownVisible(false);
        setSearchTerm('');
    };

    const roleLabel = (role) => {
        if (role === 'photographer') return 'Photographer';
        if (role === 'user') return 'Client';
        return role || 'User';
    };

    return (
        <div
            className={`search-bar-container ${isDropdownVisible && results.length > 0 ? 'is-open' : ''}`}
            ref={searchBarRef}
        >
            <span className="search-bar-icon" aria-hidden="true">⌕</span>
            <input
                type="search"
                placeholder="Search photographers, users..."
                value={searchTerm}
                onChange={(e) => {
                    setSearchTerm(e.target.value);
                    setIsDropdownVisible(true);
                }}
                onFocus={() => {
                    if (results.length > 0) {
                        setIsDropdownVisible(true);
                    }
                }}
                className="search-bar"
                aria-label="Search ViewFinders"
                aria-expanded={isDropdownVisible && results.length > 0}
            />

            {isDropdownVisible && results.length > 0 && (
                <div className="search-results" role="listbox">
                    <div className="search-results__label">People</div>
                    {results.map((result, index) => {
                        const name = buildDisplayName(result.givenName, result.familyName);
                        const initials = [result.givenName, result.familyName]
                            .filter(Boolean)
                            .map(part => part.charAt(0))
                            .join('')
                            .slice(0, 2)
                            .toUpperCase();

                        return (
                            <button
                                type="button"
                                key={`${result.profileSlug || name}-${index}`}
                                className="search-result"
                                onClick={() => handleResultClick(result.profileSlug)}
                                role="option"
                            >
                                <span className="search-result__avatar" aria-hidden="true">
                                    {initials || 'VF'}
                                </span>
                                <span className="search-result__copy">
                                    <span className="search-result__name">{name || 'ViewFinders user'}</span>
                                    <span className="search-result__meta">{roleLabel(result.role)}</span>
                                </span>
                            </button>
                        );
                    })}
                </div>
            )}
        </div>
    );
};

export default SearchBar;
